// Utils
import { getTimestamp } from '../../../../../../common/utils/timeUtil';

// Constants
import {
    RESOURCES_FETCHED,
    RESOURCES_FETCHING,
    RESOURCES_MARKED_AS_STALE,
    RESOURCES_UPDATE_FETCHED_TIME,
    ResourceStatus,
} from '../asyncResourceConstants';

// Types
import { AsyncResourceEntity, AsyncResourceEntityErrorObject } from './asyncResourceReducerTypes';
import {
    AsyncResourceFetchedAction,
    AsyncResourceFetchErrorAction,
    AsyncResourceFetchingAction,
    AsyncResourceMarkAsStaleAction,
} from '../asyncResourceActionsTypes';

export const RESOURCE_ENTITY_INITIAL_STATE: AsyncResourceEntity = {
    id: '',
    fetching: false,
    fetched: false,
    hasError: false,
    error: undefined,
    errorTime: undefined,
    errorCount: 0,
    expiry: undefined,
    status: ResourceStatus.NOT_FETCHED,
};

export const defaultAsyncResourceEntityError = (
    state = { ...RESOURCE_ENTITY_INITIAL_STATE },
    action: AsyncResourceFetchErrorAction,
    id: string,
    error: AsyncResourceEntityErrorObject,
): AsyncResourceEntity => ({
    ...state,
    id,
    status: ResourceStatus.ERRORED,
    fetching: false,
    fetched: true,
    hasError: true,
    error,
    errorTime: action.errorTime || getTimestamp(),
    errorCount: (state?.errorCount || 0) + 1,
    expiry: undefined,
});

const handleFetched = (
    state: AsyncResourceEntity,
    action: AsyncResourceFetchedAction,
    id: string,
): AsyncResourceEntity => {
    const updatedState = {
        ...state,
        id,
        status: ResourceStatus.FETCHED,
        fetching: false,
        fetched: true,
        fetchedTime: action.fetchedTime || getTimestamp(),
        hasError: false,
        error: undefined,
        errorTime: undefined,
        errorCount: 0,
    };

    if (action.expiry) {
        updatedState.expiry = action.expiry;
    }

    return updatedState;
};

const handleUpdateFetchedTime = (
    state: AsyncResourceEntity,
    action: AsyncResourceFetchedAction,
    id: string,
): AsyncResourceEntity => ({
    ...state,
    id,
    fetchedTime: action.fetchedTime || getTimestamp(),
});

const handleMarkAsStale = (
    state: AsyncResourceEntity,
    action: AsyncResourceFetchedAction,
    id: string,
): AsyncResourceEntity => ({
    ...state,
    id,
    status: ResourceStatus.STALE,
    fetching: false,
    fetched: false,
    expiry: undefined,
});

export default (
    state = { ...RESOURCE_ENTITY_INITIAL_STATE },
    action: AsyncResourceFetchingAction | AsyncResourceFetchedAction | AsyncResourceMarkAsStaleAction,
    id: string,
): AsyncResourceEntity => {
    switch (action.type) {
        case RESOURCES_FETCHING:
            return {
                ...state,
                id,
                status: ResourceStatus.FETCHING,
                fetching: true,
                expiry: undefined,
            };
        case RESOURCES_FETCHED:
            return handleFetched(state, action, id);
        case RESOURCES_MARKED_AS_STALE:
            return handleMarkAsStale(state, action, id);
        case RESOURCES_UPDATE_FETCHED_TIME:
            return handleUpdateFetchedTime(state, action, id);
        default:
            return state;
    }
};
